import React, { useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea
} from 'recharts';

const PitchChart = ({ audioDuration, Sound_Plot, Tone_Modulation, Tone_Modulation_Score }) => {
  const [windowStart, setWindowStart] = useState(0);
  const WINDOW_SIZE = 30;
  const STEP = 20;
  const SAMPLES_PER_SECOND = 800;

  const soundPlot = useMemo(() => {
    try {
      if (!Sound_Plot || !Sound_Plot["800"]) return [];

      let rawData;
      if (typeof Sound_Plot["800"] === 'string') {
        const cleanedString = Sound_Plot["800"].replace(/,\s*]$/, ']');
        rawData = JSON.parse(cleanedString);
      } else {
        rawData = Sound_Plot["800"];
      }

      if (!Array.isArray(rawData)) return [];

      return rawData.map((value, index) => {
        const parsedValue = typeof value === 'number' ? value : parseFloat(value);
        return {
          time: index / SAMPLES_PER_SECOND,
          value: isNaN(parsedValue) ? 0 : parsedValue
        };
      });
    } catch (error) {
      console.error("Error parsing Sound_Plot:", error);
      return [];
    }
  }, [Sound_Plot]);

  const toneModulation = useMemo(() => {
    try {
      let rawArray = Tone_Modulation;
      if (typeof Tone_Modulation === "object" && Tone_Modulation !== null && "Tone_Modulation_Array" in Tone_Modulation) {
        rawArray = Tone_Modulation.Tone_Modulation_Array;
      }
      if (typeof rawArray === "string") {
        rawArray = JSON.parse(rawArray.replace(/,\s*]$/, "]"));
      }
      return Array.isArray(rawArray) ? rawArray : [];
    } catch (error) {
      console.error("Error parsing Tone_Modulation:", error);
      return [];
    }
  }, [Tone_Modulation]);

  const modulationIntervals = useMemo(() => {
    if (!toneModulation.length || !audioDuration) return [];

    const fixedIntervalDuration = 5;
    const numberOfIntervals = Math.ceil(audioDuration / fixedIntervalDuration);
    const intervals = [];

    for (let i = 0; i < numberOfIntervals; i++) {
      const value = i < toneModulation.length ? toneModulation[i] : 0;
      intervals.push({
        start: i * fixedIntervalDuration,
        end: Math.min((i + 1) * fixedIntervalDuration, audioDuration),
        value
      });
    }
    return intervals;
  }, [toneModulation, audioDuration]);

  const windowEnd = Math.min(windowStart + WINDOW_SIZE, audioDuration || 0);
  const windowedData = soundPlot.filter(
    d => d.time >= windowStart && d.time <= windowEnd
  );

  return (
    <div className="pitch-chart w-full sm:h-[250px] h-[180px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={windowedData}>
          <defs>
            <linearGradient id="pitchGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2563eb" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#2563eb" stopOpacity={0.2} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" />

          {modulationIntervals.map((interval, idx) => {
            const x1 = Math.max(interval.start, windowStart);
            const x2 = Math.min(interval.end, windowEnd);
            if (x2 <= x1) return null;
            return (
              <ReferenceArea
                key={idx}
                x1={Number(x1.toFixed(2))}
                x2={Number(x2.toFixed(2))}
                fill={interval.value === 1 ? '#00C49F' : '#FF8042'}
                fillOpacity={0.2}
              />
            );
          })}

          <XAxis
            type="number"
            dataKey="time"
            domain={[windowStart, windowEnd]}
            label={{
              position: 'bottom',
              fontSize: 'sm:text-base text-xs'
            }}
            tickFormatter={(t) => Math.round(t)}
            ticks={Array.from(
              { length: Math.ceil((audioDuration || 120) / 5) + 1 },
              (_, i) => i * 5
            ).filter(tick => tick >= windowStart && tick <= windowEnd)}
            tick={{ fontSize: 'sm:text-sm text-xs' }}
          />

          <YAxis
            label={{
              value: 'Pitch Variation',
              angle: -90,
              position: 'insideLeft',
              offset: 1,
              dy: 60,
            }}
            domain={[-1.5, 1.5]}
            ticks={[-1.5, -0.75, 0, 0.75, 1.5]}
            tick={{ fontSize: 'sm:text-sm text-xs' }}
          />

          <Tooltip
            formatter={(value) => [`Value: ${value.toFixed(6)}`, '']}
            labelFormatter={(time) => `Time: ${time.toFixed(4)}s`}
            contentStyle={{ fontSize: 'sm:text-sm text-xs' }}
          />

          <Line
            type="monotone"
            dataKey="value"
            stroke="url(#pitchGradient)"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Chart Controls */}
      <div className="chart-controls px-2 sm:px-8 flex justify-center items-center mt-[-5px] pl-10">
        <div className="chart-controls px-2 sm:px-8 flex justify-center items-center mt-[-5px] pl-5">
          <div className="flex items-center justify-center gap-6 sm:gap-12 w-full max-w-[500px]">
            
            {/* Left Arrow */}
            <button
              className="text-3xl sm:text-5xl disabled:text-gray-400 transition"
              onClick={() => setWindowStart(Math.max(0, windowStart - STEP))}
              disabled={windowStart <= 0}
            >
              ←
            </button>

            {/* Good Indicator */}
            <div className="flex items-center gap-1 sm:gap-2 mt-2">
              <span className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-green-500" title="Good"></span>
              <span className="text-xs sm:text-sm text-gray-800">: Modulated</span>
            </div>

            {/* Bad Indicator */}
            <div className="flex items-center gap-1 sm:gap-2 mt-2">
              <span className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-orange-200" title="Bad"></span>
              <span className="text-xs sm:text-sm text-gray-800">: Flat</span>
            </div>

            {/* Right Arrow */}
            <button
              className="text-3xl sm:text-5xl disabled:text-gray-400 transition"
              onClick={() =>
                setWindowStart(Math.min((audioDuration || 0) - WINDOW_SIZE, windowStart + STEP))
              }
              disabled={windowStart >= (audioDuration || 0) - WINDOW_SIZE}
            >
              →
            </button>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PitchChart;
